import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MixPanel from 'mixpanel-browser';
import * as actions from '~/store/actions';
import * as actionTypes from '~/store/actions/actionTypes';
import { useTryToCall, useNavigation } from '~/hooks';
import * as lib from '~/dependencies';
import * as NuacomComponent from '~/components';
import { PERIODIC_UPDATE_CHECK_INTERVAL } from '~/config/config';
import { electron, log } from '~/helpers';
import * as NuacomScreens from '~/screens';
import { AgentStatusProvider, VideoProvider } from '~/providers';
import SubRoutes from './routes';
import { ContentNuacom, MainContent } from './styles';
import packageJson from '../../../package.json';

export default function Home(props) {
  /**
   * [REFERENCES]
   */
  const intervalElectron = useRef(null);
  const intervalContacts = useRef(null);
  const intervalCheckUpdates = useRef(null);
  const remoteVideo = useRef();

  /**
   * [LOCAL STORAGE]
   */
  const consoleLogStorage = window.localStorage.getItem('@consoleLog');

  /**
   * [HOOKS]
   */
  const tryToCall = useTryToCall();
  const [hasNoContacts, setHasNoContacts] = useState(true);
  const [
    hasSubmittedMixPanelIdentity,
    setHasSubmittedMixPanelIdentity,
  ] = useState(false);

  /**
   * [REDUX STATES - selectors]
   */
  const ua = useSelector(state => state.phone.ua);
  const contacts = useSelector(state => state.contact?.dataAll);
  const statusSystem = useSelector(state => state?.system.status);
  const consoleLog = useSelector(state => state?.system.console);
  const remoteVideoSystem = useSelector(state => state?.system.remoteVideo);
  const speakerDevice = useSelector(
    state => state?.system?.deviceManager?.audio?.speaker
  );
  const callsCurrent = useSelector(state => state.callCurrent.data);
  const volume = useSelector(state => state?.system.volume);
  const checkLastSeen = useSelector(state => state?.system.lastSeenContacts);
  const hasMandatoryCallTagging = useSelector(
    state => state.afterCallWork.mandatoryCallTagging.length
  );
  const authInfo = useSelector(state => state.auth);
  const callDetails = useSelector(state => state.call?.callDetails);
  const hiddenBottomNav = useSelector(
    state => state.conversation?.hiddenBottomNav
  );
  const { version } = useSelector(state => state.system);

  /**
   * [DISPATCH - redux function]
   */
  const dispatch = useDispatch();
  const navigate = useNavigation();

  /**
   * [MEMO - to avoid unnecessary re-renders into useEffects]
   */
  const callsCurrentMemo = useMemo(() => callsCurrent, [callsCurrent]);
  const checkLastSeenMemo = useMemo(() => checkLastSeen, [checkLastSeen]);
  const volumeMemo = useMemo(() => volume, [volume]);

  const syncStatusEvent = useCallback(() => {
    electron.changePhoneStatus(statusSystem, false);
  }, [statusSystem]);

  const forceReconnectionEvent = useCallback(() => {
    setTimeout(() => {
      ua?.transportReconnect();
      // dispatch(actions.connectWebSocket(true));
    }, 5000);
  }, [ua]);

  // activate websocket to agent status
  useEffect(() => {
    // dispatch(actions.connectWebSocket(true));

    if (window.isElectron) {
      // Send current app status to electron in order to get the badge updated
      // window.ipcRenderer.once('update-phone-status-required', () => {
      //   electron.changePhoneStatus(statusSystem, false);
      // });

      // Event used to the call-events and sip reconnection
      window.ipcRenderer.once('force-reconnection', forceReconnectionEvent);

      // Electron call this event to sync the app status.
      window.ipcRenderer.on('sync-status', syncStatusEvent);

      // Electron call this event when user click in a given messaging notification
      window.ipcRenderer.on(
        'view-conversation-message',
        (_, { conversationId }) => {
          navigate.chatView({ id: conversationId });
        }
      );
    }

    return () => {
      // dispatch(actions.disconnectWebSocket());
      if (window.isElectron) {
        window.ipcRenderer.removeListener(
          'force-reconnection',
          forceReconnectionEvent
        );
        window.ipcRenderer.removeListener('sync-status', syncStatusEvent);
      }
    };
  }, [dispatch, syncStatusEvent, forceReconnectionEvent, navigate]);

  useEffect(() => {
    if (
      authInfo &&
      authInfo?.account_id &&
      authInfo?.user_extension &&
      authInfo?.userIdCode &&
      version &&
      !hasSubmittedMixPanelIdentity
    ) {
      MixPanel.identify(authInfo.userIdCode);
      MixPanel.people.set_once({
        account_id: authInfo.account_id,
        user_extension: authInfo.user_extension,
      });

      const sharedProperties = {
        app_version: version,
        platform: navigator?.userAgentData?.platform || 'unknown',
      };

      MixPanel.people.set(sharedProperties);
      MixPanel.register(sharedProperties);
      setHasSubmittedMixPanelIdentity(true);
    }
  }, [
    authInfo,
    version,
    hasSubmittedMixPanelIdentity,
    setHasSubmittedMixPanelIdentity,
  ]);

  // Bring the contacts for the very first time.
  useEffect(() => {
    if (contacts && hasNoContacts && contacts.length === 0) {
      // dispatch(actions.getContacts('All'));
      dispatch(actions.getContacts('Personal'));
      setHasNoContacts(false);
    }
  }, [dispatch, contacts, setHasNoContacts, hasNoContacts]);

  // check if contact are up-to-date
  const loadContactToUpdate = useCallback(() => {
    const today = new Date();
    const dateNow = lib.dayjs(today).format('YYYY-MM-DD HH:mm:ss');
    if (
      lib.dayjs(dateNow).diff(lib.dayjs(checkLastSeenMemo), 'minutes') >= 15
    ) {
      dispatch(actions.checkNewContacts('All'));
      dispatch(actions.checkNewContacts('Personal'));
    }
  }, [checkLastSeenMemo, dispatch]);

  useEffect(() => {
    intervalContacts.current = setInterval(() => {
      loadContactToUpdate();
    }, 1000 * 60);

    return () => {
      clearInterval(intervalContacts.current);
      intervalContacts.current = null;
    };
  }, [loadContactToUpdate]);

  // Click to call observation
  useEffect(() => {
    if (window.isElectron) {
      intervalElectron.current = setInterval(() => {
        try {
          const { AVAILABLE, BUSY } = actionTypes.STATUS;
          if (![AVAILABLE, BUSY].some(status => statusSystem.includes(status)))
            return; // If neither AVAILABLE nor BUSY are found, exit making calls via protocol

          if (hasMandatoryCallTagging) return; // Skip making call while in mandatory tagging screen

          const defaultProtocol = 'nuacom:';
          const protocols = ['tel:', 'call:', 'callto:', 'sip:', 'nuacom:'];

          const { textValue } =
            window.ipcRenderer.sendSync('makeCallbyProtocol') || {};

          if (typeof textValue !== 'string') return; // Skip null's
          if (textValue.includes('authenticate')) return; // Skip SSO authenticate
          if (!protocols.some(protocol => textValue.startsWith(protocol)))
            return; // If not starts with allowed protocol

          log.debug('Making call via protocol. ', textValue);

          const urlDetails = new URL(textValue);

          // Replace all possible / e.g from //600 to 600 & white espaces and other special caracters except + ones
          const phoneNumber = decodeURIComponent(urlDetails.pathname).replace(
            /[^0-9+*#]/g,
            ''
          );

          if (phoneNumber) tryToCall(phoneNumber);
          window.ipcRenderer.send('resetProtocol');

          // Handle integrations
          if (
            urlDetails.protocol === defaultProtocol &&
            urlDetails.searchParams.get('integration') === 'salesforce'
          ) {
            const contactId = urlDetails.searchParams.get('contactId');
            dispatch({
              type: actionTypes.SET_SALESFORCE_DISPLAY_SPECIFIC_EXTERNAL_ID,
              externalId: contactId,
            });
          }
        } catch (err) {
          log.error('[ELECTRON] Failed to handing custom protocols.', err);
        }
      }, 1000);
    }

    return () => {
      clearInterval(intervalElectron.current);
      intervalElectron.current = null;
    };
  }, [dispatch, statusSystem, tryToCall, hasMandatoryCallTagging]);

  // verify if console log state saved on localstorage is different from redux state and update
  useEffect(() => {
    if (consoleLogStorage !== consoleLog) {
      dispatch(actions.Console(consoleLogStorage !== 'false'));
    }
  }, [consoleLog, consoleLogStorage, dispatch]);

  // Start new configurations, verify microphone activities
  useEffect(() => {
    dispatch(actions.startUA());
    dispatch(actions.phoneDeviceSetup());
    dispatch(actions.fetchFavoriteAgents());
    dispatch(actions.fetchExtensions());
    dispatch(actions.fetchAccountInfo());
    dispatch(actions.setOrganizationsAndGroups());
    dispatch(actions.numberToTransfer());
    // dispatch(actions.connectWebSocket(true));
    dispatch(actions.fetchAfterCallRules());

    // return () => dispatch(actions.disconnectWebSocket());
  }, [dispatch]);

  // Setting new source remote
  useEffect(() => {
    (async function playPromise() {
      if (remoteVideoSystem?.active) {
        remoteVideo.current.srcObject = remoteVideoSystem;
        // Play should returns a promise: https://bit.ly/3iw5YVv
        await remoteVideo.current.play();
      }
    })();
  }, [remoteVideoSystem]);

  // Verify for new calls
  useEffect(() => {
    // Clear remote video when have no calls anymore
    if (callsCurrentMemo.length === 0) {
      dispatch(actions.clearAllMediaStream());
      // Clear remote video element reference
      (function stopPromise() {
        if (remoteVideo.current.srcObject) {
          remoteVideo.current.srcObject
            .getTracks()
            .forEach(track => track.stop());
          remoteVideo.current.pause();
          remoteVideo.current.srcObject = null;
        }
      })();
    }
  }, [callsCurrentMemo, dispatch]);

  // adjust volume
  useEffect(() => {
    if (volumeMemo) {
      remoteVideo.current.volume = volumeMemo;
    }
  }, [volumeMemo]);

  // set audio device to speaker after call answered
  useEffect(() => {
    // Some browser does not support setSinkId
    if (
      speakerDevice &&
      remoteVideo?.current &&
      typeof remoteVideo.current?.setSinkId === 'function'
    ) {
      remoteVideo.current.setSinkId(speakerDevice.deviceId);
    }
  }, [speakerDevice]);

  useEffect(() => {
    // again because google chrome don't insert first hash into history
    window.location.hash = '#';
    window.location.hash = '##';
    // eslint-disable-next-line
    window.onhashchange = function() {
      window.location.hash = '#';
    };
  }, []);

  // Check for updates
  useEffect(() => {
    dispatch(actions.checkForUpdates());
    dispatch(actions.systemUpdateDateTime());

    dispatch(actions.checkUpdater());
    intervalCheckUpdates.current = setInterval(() => {
      dispatch(actions.checkUpdater());
    }, PERIODIC_UPDATE_CHECK_INTERVAL);

    if (window.isElectron) {
      electron.storeLoadedWebVersion(packageJson?.version);
    }

    return () => {
      clearInterval(intervalCheckUpdates.current);
      intervalCheckUpdates.current = null;
    };
  }, [dispatch]);

  return (
    <div id="contentNuacom">
      <NuacomComponent.Sounds />
      <div id="mainContent">
        <ContentNuacom>
          <video ref={remoteVideo} style={{ display: 'none' }} autoPlay />
          <MainContent>
            {/* Call details can open for every sub routes */}
            {callDetails.map(e => (
              <NuacomScreens.CallDetails key={e.id} itemDetails={e} />
            ))}
            <VideoProvider>
              <AgentStatusProvider>
                <SubRoutes {...props} />
              </AgentStatusProvider>
            </VideoProvider>
          </MainContent>
          {!hiddenBottomNav ? <NuacomComponent.BottomNav {...props} /> : null}
        </ContentNuacom>
      </div>
    </div>
  );
}

Home.propTypes = {
  match: lib.PropTypes.instanceOf(Object).isRequired,
  history: lib.PropTypes.instanceOf(Object).isRequired,
};
